import { eachDayOfInterval, endOfWeek, format, isSameDay, startOfWeek } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { dateLocale } from '@/lib/dateLocale'
import type { Appointment, Client } from '@/lib/api'
import { AppointmentEvent } from './AppointmentEvent'
import { TimeGrid } from './TimeGrid'
import { assignColumns, minutesToGridY, parseTime } from './calendarHelpers'
import { COMPRESSED_NIGHT_HEIGHT, DAY_START_HOUR, NIGHT_START_HOUR, eventColor } from './constants'

export interface WeekViewProps {
  /** 周锚点日期 */
  date: Date
  /** 按天聚合的预约映射（key: yyyy-MM-dd） */
  byDay: Map<string, Appointment[]>
  /** 学员映射 */
  clientsMap: Map<number, Client>
  onEventClick: (appt: Appointment) => void
  onSlotClick: (date: Date, time: string) => void
  onEventDrop?: (appt: Appointment, date: Date, time: string) => void
}

const TIME_AXIS_WIDTH = 'w-14'
const HOUR_LABELS = Array.from({ length: NIGHT_START_HOUR - DAY_START_HOUR }, (_, i) => i + DAY_START_HOUR)

export function WeekView({ date, byDay, clientsMap, onEventClick, onSlotClick, onEventDrop }: WeekViewProps) {
  const { i18n } = useTranslation()
  const locale = dateLocale(i18n.language)
  const days = eachDayOfInterval({
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  })
  const today = new Date()

  const compressedRowTop = minutesToGridY(NIGHT_START_HOUR * 60, true)

  return (
    <div className="flex flex-col h-full" data-testid="week-view">
      {/* 统一滚动区域：表头 + 时间轴 + 7 列共用同一个滚动条。
          表头内置于滚动容器并 sticky：与网格列同处一个内容宽度，
          避免滚动条占位导致表头分隔线与网格竖线错位 */}
      <div className="flex-1 overflow-y-auto">
        {/* 表头：左侧时间轴占位 + 7 天 */}
        <div className="sticky top-0 z-20 flex border-b border-slate-200 bg-white">
          <div className={`${TIME_AXIS_WIDTH} shrink-0 border-r border-slate-200 bg-slate-50`} />
          {days.map((day) => {
            const key = format(day, 'yyyy-MM-dd')
            const isToday = isSameDay(day, today)
            return (
              <div
                key={`header-${key}`}
                className="flex-1 min-w-0 h-12 flex items-center justify-center text-sm font-medium border-r border-slate-200 last:border-r-0 bg-white"
              >
                <div className={`text-center px-1 ${isToday ? 'text-primary font-bold' : ''}`}>
                  <div className="text-xs text-slate-500">{format(day, 'EEE', { locale })}</div>
                  <div className="text-sm">{format(day, 'dd')}</div>
                </div>
              </div>
            )
          })}
        </div>

        {/* min-h-full：行高随网格内容撑开，列分割线才能贯穿全部滚动区域 */}
        <div className="flex min-h-full">
          {/* 左侧时间轴 */}
          <div className={`${TIME_AXIS_WIDTH} shrink-0 border-r border-slate-200 bg-slate-50 relative`}>
            {HOUR_LABELS.map((h, idx) => {
              const top = minutesToGridY(h * 60, true)
              // Google Calendar 风格：标签贴在自己那根线的上方；
              // 首行 08:00 上方无空间，保持线下方避免被滚动容器裁切
              const isFirst = idx === 0
              return (
                <div
                  key={h}
                  className={`absolute right-0 text-xs text-slate-400 pr-1 ${isFirst ? '' : '-translate-y-full'}`}
                  style={{ top: isFirst ? top + 2 : top }}
                >
                  {String(h).padStart(2, '0')}:00
                </div>
              )
            })}
            <div
              className="absolute right-0 text-xs text-slate-400 text-right pr-1 leading-tight"
              style={{ top: compressedRowTop + COMPRESSED_NIGHT_HEIGHT / 2, transform: 'translateY(-50%)' }}
            >
              22:00-
              <br />
              08:00
            </div>
          </div>

          {days.map((day) => {
            const key = format(day, 'yyyy-MM-dd')
            const appointments = byDay.get(key) || []
            const positioned = assignColumns(appointments)

            // 分割线在包裹层：TimeGrid 根节点是其唯一子元素，last: 恒命中会吃掉 border-r；
            // 7 个包裹层互为兄弟，last:border-r-0 只去掉最后一列
            return (
              <div key={key} className="flex-1 min-w-0 border-r border-slate-200 last:border-r-0">
                <TimeGrid
                  date={day}
                  showHeader={false}
                  scrollable={false}
                  compressedNight
                  onSlotClick={onSlotClick}
                  onDrop={
                    onEventDrop
                      ? (d, t, payload) => {
                          if (payload && typeof payload === 'object' && 'id' in payload) {
                            onEventDrop(payload as Appointment, d, t)
                          }
                        }
                      : undefined
                  }
                >
                  {positioned.map((appt) => {
                    const widthPct = 100 / appt.colCount
                    const leftPct = appt.col * widthPct
                    const top = minutesToGridY(parseTime(appt.startTime), true)
                    const height = Math.max(16, minutesToGridY(parseTime(appt.endTime), true) - top)
                    return (
                      <AppointmentEvent
                        key={appt.id}
                        appt={appt}
                        client={appt.clientId ? clientsMap.get(appt.clientId) : undefined}
                        color={eventColor(appt)}
                        style={{
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          top: top + 2,
                          height: height - 4,
                        }}
                        draggable={appt.status === 'scheduled'}
                        onClick={onEventClick}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', String(appt.id))
                          e.dataTransfer.setData('application/json', JSON.stringify({
                            id: appt.id,
                            scheduledDate: appt.scheduledDate,
                            startTime: appt.startTime,
                            endTime: appt.endTime,
                          }))
                        }}
                      />
                    )
                  })}
                </TimeGrid>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
